import { useState } from "react";

type Mode = {
  key: string;
  label: string;
  tagline: string;
  who: string;
  ai: string;
  gate: string;
  gateState: "you" | "review" | "stamp";
};

const MODES: Mode[] = [
  {
    key: "manual",
    label: "Manual",
    tagline: "You drive. The engine checks.",
    who: "You size each member yourself. The engine verifies every choice against the governing code clause and shows the math.",
    ai: "stays out of the way — available when you ask, silent when you don't.",
    gate: "Nothing runs without you",
    gateState: "you",
  },
  {
    key: "automated",
    label: "Automated",
    tagline: "The engine sizes to your standards.",
    who: "Your saved design profile drives the run. The engine sizes and checks the whole structure; you review the package.",
    ai: "watches for outliers and surfaces the governing case, but takes no action on its own.",
    gate: "Engine sizes · you review",
    gateState: "review",
  },
  {
    key: "ai",
    label: "AI",
    tagline: "AI supervises. You stamp.",
    who: "AI runs the workflow end-to-end, sizing within your standards and resolving the routine calls.",
    ai: "asks when a decision is judgment, flags what it's unsure of, learns your preferences, and logs every move. It never stamps.",
    gate: "AI supervises · you stamp",
    gateState: "stamp",
  },
];

export function ControlSection() {
  const [mode, setMode] = useState(MODES[1].key);
  const active = MODES.find((m) => m.key === mode) ?? MODES[1];

  return (
    <section className="landing-wrap landing-section" id="control-modes">
      <div className="section-head">
        <span className="section-eyebrow">Three control modes</span>
        <h2 className="section-title">Automation is a dial, not a switch.</h2>
        <p className="section-sub">
          Each position changes who drives: you, your saved standards, or AI under your
          supervision. Turn it up or down per project. The one thing that never moves: the
          approval gate.
        </p>
      </div>

      <div className="dial" role="group" aria-label="Control mode details">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`dial-btn${mode === m.key ? " is-on" : ""}`}
            aria-pressed={mode === m.key}
            onClick={() => setMode(m.key)}
          >
            <span className="dial-btn-label">{m.label}</span>
            <span className="dial-btn-tag">{m.tagline}</span>
          </button>
        ))}
      </div>

      <div className="dial-detail" aria-live="polite" aria-atomic="true">
        <div>
          <p className="dial-detail-who">{active.who}</p>
          <p className="dial-detail-ai">
            <span className="dial-ai-label">AI</span> {active.ai}
          </p>
        </div>
        <div className={`gate-card gate-card--${active.gateState}`}>
          <span className="gate-label">Approval gate</span>
          <span className="gate-state">
            <span className="gate-dot" aria-hidden="true" />
            {active.gate}
          </span>
          <span className="gate-foot">The licensed engineer of record is always you.</span>
        </div>
      </div>
    </section>
  );
}
